import type { WeekDateRangeResponse } from '../api/generated';
import type { EconomicsPeriod } from '../types/economicsTypes';
import { dateToIsoString } from '../utils/dateUtils';

const EMPTY_PERIOD: EconomicsPeriod = { from: 0, to: 0 };

// Every week the year plan knows about — the fallback whenever "the current week" has no
// meaning for the loaded year.
export function getFullPeriod(weeks: WeekDateRangeResponse[]): EconomicsPeriod {
  if (weeks.length === 0) return EMPTY_PERIOD;
  const numbers = weeks.map((week) => week.weekNumber);
  return { from: Math.min(...numbers), to: Math.max(...numbers) };
}

// The period the page opens on: just the week containing today, so planners land on the
// work in front of them rather than the whole year (REH-371).
//
// The match is by date range, not by ISO week number, which is what keeps a plan for
// another year honest: the backend's weeks are clamped to Jan 1 - Dec 31, so exactly one
// week contains today for the current year and none does for any other — and that miss
// falls back to the full span.
//
// Both sides are plain YYYY-MM-DD (the backend sends LocalDate), so they compare
// lexicographically; `today` is read from local calendar components for the same reason
// dateUtils avoids toISOString().
export function getDefaultPeriod(
  weeks: WeekDateRangeResponse[],
  today: Date = new Date()
): EconomicsPeriod {
  if (weeks.length === 0) return EMPTY_PERIOD;
  const todayIso = dateToIsoString(today);
  const currentWeek = weeks.find((week) => week.startDate <= todayIso && todayIso <= week.endDate);
  if (!currentWeek) return getFullPeriod(weeks);
  return { from: currentWeek.weekNumber, to: currentWeek.weekNumber };
}
